import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    operations: true,
    compliance: true,
    analytics: true
  });

  const navigationSections = [
    {
      id: 'operations',
      title: 'Asset Operations',
      items: [
        {
          label: 'Asset Dashboard',
          path: '/asset-management-dashboard',
          icon: 'LayoutDashboard',
          tooltip: 'Main asset management dashboard'
        },
        {
          label: 'Asset Details',
          path: '/asset-detail-management',
          icon: 'Package',
          tooltip: 'Detailed asset record management'
        }
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance & Maintenance',
      items: [
        {
          label: 'Audit Workflow',
          path: '/quarterly-audit-workflow',
          icon: 'ClipboardCheck',
          tooltip: 'Quarterly audit processes'
        },
        {
          label: 'Warranty Center',
          path: '/warranty-management-center',
          icon: 'Shield',
          tooltip: 'Warranty management and tracking'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Administration',
      items: [
        {
          label: 'Analytics Dashboard',
          path: '/asset-analytics-dashboard',
          icon: 'BarChart3',
          tooltip: 'Asset analytics and insights'
        },
        {
          label: 'User Management',
          path: '/user-management-console',
          icon: 'Users',
          tooltip: 'User management console'
        },
        {
          label: 'System Config',
          path: '/system-configuration-panel',
          icon: 'Settings',
          tooltip: 'System configuration panel'
        }
      ]
    }
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleSection = (sectionId) => {
    if (!isCollapsed) {
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: !prev?.[sectionId]
      }));
    }
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  return (
    <aside className={`fixed left-0 top-16 bottom-0 z-40 bg-card border-r border-border transition-layout ${
      isCollapsed ? 'w-16' : 'w-72'
    }`}>
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Navigation
            </h2>
          )}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-8 w-8"
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={16} 
              />
            </Button>
          )}
        </div>

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-6">
            {navigationSections?.map((section) => (
              <div key={section?.id} className="px-3">
                {/* Section Header */}
                {!isCollapsed && (
                  <button
                    onClick={() => toggleSection(section?.id)}
                    className="flex items-center justify-between w-full px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide hover:text-foreground transition-smooth"
                  >
                    <span>{section?.title}</span>
                    <Icon 
                      name={expandedSections?.[section?.id] ? "ChevronDown" : "ChevronRight"} 
                      size={14} 
                    />
                  </button>
                )}

                {/* Section Items */}
                <div className={`space-y-1 ${
                  isCollapsed ? 'mt-0' : expandedSections?.[section?.id] ? 'mt-2' : 'hidden'
                }`}>
                  {section?.items?.map((item) => {
                    const isActive = isActiveRoute(item?.path);
                    
                    return (
                      <div key={item?.path} className="relative group">
                        <button
                          onClick={() => handleNavigation(item?.path)}
                          className={`flex items-center w-full px-3 py-2.5 text-sm rounded-md transition-smooth ${
                            isActive
                              ? 'bg-primary text-primary-foreground shadow-subtle'
                              : 'text-foreground hover:bg-muted hover:text-foreground'
                          } ${isCollapsed ? 'justify-center' : 'justify-start space-x-3'}`}
                          title={isCollapsed ? item?.tooltip : ''}
                        >
                          <Icon 
                            name={item?.icon} 
                            size={18} 
                            className={isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}
                          />
                          {!isCollapsed && (
                            <span className="font-medium">{item?.label}</span>
                          )}
                        </button>
                        {/* Tooltip for collapsed state */}
                        {isCollapsed && (
                          <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-elevated opacity-0 group-hover:opacity-100 transition-smooth pointer-events-none whitespace-nowrap z-50">
                            {item?.label}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Section Separator */}
                {!isCollapsed && section?.id !== 'analytics' && (
                  <div className="mt-4 border-b border-border" />
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border">
          {!isCollapsed ? (
            <div className="flex items-center space-x-3 text-xs text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>Last updated: Aug 19, 2025</span>
            </div>
          ) : (
            <div className="flex justify-center">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;